import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleDashed, X } from 'lucide-react';
import { ListStage, LIST_UNIT_LABELS, WS_EVENT, type ListUnit } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useEventoSocket } from '@/lib/socket';
import {
  useListaDelPedidoComercio,
  useRevisarRenglon,
  useTerminarLista,
  type ItemLista,
  type PedidoComercio,
} from '@/lib/queries';
import { Boton, Entrada, EntradaDinero, Modal } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/**
 * El comercio cotiza la lista renglón por renglón.
 *
 * Cada renglón se resuelve aquí: hay (con su precio) o no hay (con reemplazo
 * sugerido si el cliente lo permitió). Cada decisión le aparece al cliente
 * en vivo. Cuando no queda nada pendiente, «Terminar» le manda el total para
 * que lo confirme; hasta entonces se puede corregir lo que sea.
 */
export function RevisarLista({
  pedido,
  abierto,
  onCerrar,
}: {
  pedido: PedidoComercio;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const avisos = useAvisos();
  const cliente = useQueryClient();
  const lista = useListaDelPedidoComercio(abierto ? pedido.id : null);
  const terminar = useTerminarLista(pedido.id);

  useEventoSocket<{ orderId: string }>(WS_EVENT.LIST_UPDATED, (evento) => {
    if (evento.orderId !== pedido.id) return;
    void cliente.invalidateQueries({ queryKey: ['pedido-lista', pedido.id] });
  });

  const l = lista.data;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={`Cotizar la lista de ${pedido.code}`}
      descripcion="Cada renglón que resuelvas le aparece al cliente al instante."
      ancho="lg"
    >
      <div className="max-h-[75vh] space-y-3 overflow-y-auto p-5">
        {!!l?.budgetCap && (
          <p
            className={cn(
              'rounded-kick px-3 py-2 text-sm',
              l.subtotal > l.budgetCap
                ? 'bg-red-50 font-semibold text-kick-red'
                : 'bg-kick-light text-kick-gray',
            )}
          >
            El cliente fijó un tope de {formatCOP(l.budgetCap)}.
            {l.subtotal > l.budgetCap && ` Vas en ${formatCOP(l.subtotal)}: ya lo superaste.`}
          </p>
        )}
        {l?.stage === ListStage.AWAITING_APPROVAL && (
          <p className="rounded-kick bg-blue-50 px-3 py-2 text-sm text-blue-800">
            Cotización enviada: el cliente está decidiendo. Todavía puedes corregir renglones.
          </p>
        )}
        {l?.stage === ListStage.APPROVED && (
          <p className="rounded-kick bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            El cliente confirmó {formatCOP(l.total)}. Acepta el pedido en el tablero y prepáralo.
          </p>
        )}

        {(l?.items ?? []).map((item) => (
          <Renglon
            key={item.id}
            item={item}
            orderId={pedido.id}
            soloLectura={l?.stage === ListStage.APPROVED}
          />
        ))}

        {l && (
          <div className="sticky bottom-0 -mx-5 border-t border-kick-gray/15 bg-white px-5 pb-1 pt-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-kick-gray">
                Productos: <strong className="text-kick-ink">{formatCOP(l.subtotal)}</strong> ·
                Total con domicilio:{' '}
                <strong className="text-kick-ink">{formatCOP(l.total)}</strong>
              </span>
              {l.stage === ListStage.QUOTING && (
                <Boton
                  cargando={terminar.isPending}
                  disabled={l.pendientes > 0}
                  onClick={() =>
                    terminar.mutate(undefined, {
                      onSuccess: () =>
                        avisos.exito('Cotización enviada: el cliente confirma el total'),
                      onError: (e) => avisos.error(getErrorMessage(e)),
                    })
                  }
                >
                  {l.pendientes > 0
                    ? `Faltan ${l.pendientes} renglón${l.pendientes === 1 ? '' : 'es'}`
                    : 'Terminar y enviar al cliente'}
                </Boton>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Renglon({
  item,
  orderId,
  soloLectura,
}: {
  item: ItemLista;
  orderId: string;
  soloLectura: boolean;
}) {
  const avisos = useAvisos();
  const revisar = useRevisarRenglon(orderId);
  const [precio, setPrecio] = useState(item.price ?? 0);
  const [sinStock, setSinStock] = useState(item.status === 'UNAVAILABLE');
  const [sugerencia, setSugerencia] = useState(item.suggestionName ?? '');
  const [precioSugerencia, setPrecioSugerencia] = useState(item.suggestionPrice ?? 0);

  // Si otro del equipo lo resolvió, el renglón se pone al día solo.
  useEffect(() => {
    setPrecio(item.price ?? 0);
    setSinStock(item.status === 'UNAVAILABLE');
    setSugerencia(item.suggestionName ?? '');
    setPrecioSugerencia(item.suggestionPrice ?? 0);
  }, [item.status, item.price, item.suggestionName, item.suggestionPrice]);

  const guardar = (comoNoHay: boolean): void => {
    revisar.mutate(
      comoNoHay
        ? {
            itemId: item.id,
            status: 'UNAVAILABLE',
            ...(item.allowSubstitute && sugerencia.trim()
              ? { suggestionName: sugerencia.trim(), suggestionPrice: precioSugerencia }
              : {}),
          }
        : { itemId: item.id, status: 'CONFIRMED', price: precio },
      { onError: (e) => avisos.error(getErrorMessage(e)) },
    );
  };

  return (
    <article
      className={cn(
        'rounded-kick border p-3',
        item.status === 'PENDING' ? 'border-amber-200 bg-amber-50/40' : 'border-kick-gray/15',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="mt-0.5 shrink-0">
          {item.status === 'PENDING' && <CircleDashed className="h-4 w-4 text-amber-600" />}
          {item.status === 'CONFIRMED' && <Check className="h-4 w-4 text-emerald-600" />}
          {item.status === 'UNAVAILABLE' && <X className="h-4 w-4 text-kick-red" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-kick-ink">
            {item.quantity} {LIST_UNIT_LABELS[item.unit as ListUnit] ?? ''} · {item.name}
          </p>
          {item.note && <p className="text-xs italic text-kick-gray">«{item.note}»</p>}
          {item.suggestionDecision === 'ACCEPTED' && (
            <p className="text-xs font-semibold text-emerald-700">
              El cliente aceptó el reemplazo
            </p>
          )}
          {item.suggestionDecision === 'REJECTED' && (
            <p className="text-xs text-kick-gray">El cliente rechazó el reemplazo</p>
          )}
        </div>

        {!soloLectura && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-36">
              <EntradaDinero centavos={precio} onCambio={setPrecio} />
            </div>
            <Boton
              className="px-3 py-1.5 text-xs"
              cargando={revisar.isPending}
              disabled={precio <= 0}
              onClick={() => {
                setSinStock(false);
                guardar(false);
              }}
            >
              Hay: {precio > 0 ? formatCOP(precio) : 'ponle precio'}
            </Boton>
            <Boton
              variante="secundario"
              className="px-3 py-1.5 text-xs"
              onClick={() => setSinStock((v) => !v)}
            >
              No hay
            </Boton>
          </div>
        )}
      </div>

      {sinStock && !soloLectura && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-kick bg-kick-light p-2.5">
          {item.allowSubstitute ? (
            <>
              <Entrada
                value={sugerencia}
                onChange={(e) => setSugerencia(e.target.value)}
                maxLength={200}
                placeholder="Sugiere un reemplazo (opcional)"
                className="min-w-48 flex-1"
              />
              <div className="w-36">
                <EntradaDinero centavos={precioSugerencia} onCambio={setPrecioSugerencia} />
              </div>
            </>
          ) : (
            <p className="flex-1 text-xs text-kick-gray">
              El cliente pidió que, si no hay, se quite sin reemplazo.
            </p>
          )}
          <Boton
            variante="peligro"
            className="px-3 py-1.5 text-xs"
            cargando={revisar.isPending}
            disabled={!!sugerencia.trim() && precioSugerencia <= 0}
            onClick={() => guardar(true)}
          >
            Marcar no hay
          </Boton>
        </div>
      )}
    </article>
  );
}
